<template>
	<form @submit.prevent="onSubmit">
		<Card>
			<h3>Competition details</h3>
			<div class="fieldset">
				<FieldInput name="name" required v-model="value.name" label="Name" />
				<FieldDateRange required v-model="value.dates" label="Running dates" />
				<div>
					<FieldCheckbox name="enabled" v-model="value.enabled">Enabled</FieldCheckbox>
					<Note v-if="value.enabled"
						>Enabled competitions start and end automatically between their running dates.</Note
					>
					<Note v-else
						>Disabled competitions aren't active between their running dates until enabled
						manually.</Note
					>
				</div>
				<FieldInput
					name="linkTerms"
					required
					v-model="value.linkTerms"
					label="Terms & conditions URL"
				/>
				<FieldInput
					name="linkPrivacy"
					required
					v-model="value.linkPrivacy"
					label="Privacy policy URL"
				/>
			</div>
		</Card>
		<Card>
			<h3>Entry form</h3>
			<p>
				Customise the entry form for this competition by choosing which form fields are shown to
				participants.
			</p>
			<div class="fieldset">
				<div class="row">
					<div class="col">
						<FieldCheckbox name="formName" disabled v-model="value.entryForm.name"
							>Full name</FieldCheckbox
						>
						<Note>Always required.</Note>
					</div>
					<div class="col">
						<FieldCheckbox name="formEmail" disabled v-model="value.entryForm.email"
							>Email</FieldCheckbox
						>
						<Note>Always required.</Note>
					</div>
				</div>
				<div class="row">
					<FieldCheckbox name="formAge" v-model="value.entryForm.age">Age</FieldCheckbox>
					<FieldCheckbox name="formMobile" v-model="value.entryForm.mobile">Mobile</FieldCheckbox>
				</div>
				<div class="row">
					<FieldCheckbox name="formPostcode" v-model="value.entryForm.postcode"
						>Postcode</FieldCheckbox
					>
					<FieldCheckbox name="formAddress" v-model="value.entryForm.address"
						>Mailing address</FieldCheckbox
					>
				</div>
				<div class="row">
					<FieldCheckbox name="formSchemeId" v-model="value.entryForm.schemeId"
						>Scheme ID</FieldCheckbox
					>
				</div>
			</div>
		</Card>
		<Card>
			<h3>Prizes</h3>
			<p>
				Add prizes to this competition. All images are cropped to fit a circle (square images work
				best).
			</p>
			<PrizesForm v-model="value.prizes" />
			<Note>Supported image types: JPG, PNG, GIF</Note>
		</Card>
		<Card>
			<h3>Customise messaging</h3>
			<p>
				Edit content that appears in Recycle Race while this competition is active.
			</p>
			<Accordion>
				<AccordionItem label="Prize card*">
					<p>
						Tells players about the prizes they could win by playing during this competition.
						Appears on the home screen above the leaderboards.
					</p>
					<div class="accordion-inner">
						<div>
							<FieldInput
								label="Title*"
								name="panelPrizeTitle"
								v-model="value.panels[panels.PRIZE_TITLE]"
							/>
							<Note>Should be no more than four words.</Note>
						</div>
						<FieldTextarea
							label="Content"
							name="panelPrizeBody"
							v-model="value.panels[panels.PRIZE_BODY]"
						/>
					</div>
				</AccordionItem>
				<AccordionItem label="Enter details card*">
					<p>
						Encourages players to enter their details to win. Appears on the game over screen and
						above the enter details form.
					</p>
					<div class="accordion-inner">
						<div>
							<FieldInput
								label="Title*"
								name="panelEnterDetailsTitle"
								v-model="value.panels[panels.ENTER_DETAILS_TITLE]"
							/>
							<Note>Should be no more than four words.</Note>
						</div>
						<FieldTextarea
							label="Content"
							name="panelEnterDetailsBody"
							v-model="value.panels[panels.ENTER_DETAILS_BODY]"
						/>
					</div>
				</AccordionItem>
				<AccordionItem label="Thanks for entering card*">
					<p>Shown to players after they have submitted their details.</p>
					<div class="accordion-inner">
						<div>
							<FieldInput
								label="Title*"
								name="panelThanksTitle"
								v-model="value.panels[panels.THANKS_TITLE]"
							/>
							<Note>Should be no more than four words.</Note>
						</div>
						<FieldTextarea
							label="Content"
							name="panelThanksBody"
							v-model="value.panels[panels.THANKS_BODY]"
						/>
					</div>
				</AccordionItem>
			</Accordion>
		</Card>
		<Card>
			<h3>Leaderboards*</h3>
			<p>Choose which leaderboards should be shown while this competition is active.</p>
			<LeaderboardsForm v-model="value.leaderboards" :ranges="leaderboardRanges" />
		</Card>
		<SaveCancel
			:saveLabel="edit ? 'Update' : 'Create'"
			:invalid="invalid"
			:busy="busy"
			@save="onSubmit"
			@cancel="onCancel"
		/>
	</form>
</template>

<script>
import { LeaderboardRange, AppPanel } from 'recycle-race-common/lib/enums'
import Card from '@/components/Card'
import FieldInput from '@/components/FieldInput'
import FieldTextarea from '@/components/FieldTextarea'
import FieldDateRange from '@/components/FieldDateRange'
import FieldCheckbox from '@/components/FieldCheckbox'
import Note from '@/components/Note'
import Accordion from '@/components/Accordion'
import AccordionItem from '@/components/AccordionItem'
import SaveCancel from '@/components/SaveCancel'

import LeaderboardsForm from '@/forms/LeaderboardsForm'
import PrizesForm from '@/forms/PrizesForm'

import { vEvent } from '@/lib/validators'

export default {
	name: 'EventForm',
	props: {
		value: Object,
		busy: Boolean,
		edit: Boolean,
	},
	data() {
		return {
			panels: AppPanel,
			leaderboardRanges: [
				LeaderboardRange.DAILY,
				LeaderboardRange.WEEKLY,
				LeaderboardRange.MONTHLY,
				LeaderboardRange.EVENT_SPAN,
			],
		}
	},
	validations: {
		value: vEvent,
	},
	computed: {
		invalid() {
			return this.$v.$invalid
		},
	},
	methods: {
		onSubmit() {
			if (!this.invalid) {
				this.$emit('submit')
			}
		},
		onCancel() {
			this.$router.push({ name: 'Manage Competitions' })
		},
	},
	components: {
		Card,
		FieldInput,
		FieldTextarea,
		FieldDateRange,
		FieldCheckbox,
		Note,
		Accordion,
		AccordionItem,
		SaveCancel,
		LeaderboardsForm,
		PrizesForm,
	},
}
</script>

<style lang="scss" scoped>
@import '~scss/layout';

form {
	@include form-spacing;

	.row {
		@include row($gap-v: 1rem);

		> .col {
			@include truncate-margin;
		}
	}

	.accordion-inner {
		@include not-last {
			margin-bottom: 1rem;
		}
	}

	@include mq(md) {
		.row {
			@include row($gap-v: 2rem);
		}
	}
}
</style>
